import "./KitchenStationForm.scss"

import React from "react"
import { Link, Redirect } from "react-router-dom"
import request from "request"
import usesFeatures from "usesFeatures"

import Header from "Header"
import Form from "Form"
import Input from "Input"
import StoreTerminalSelector from "StoreTerminalSelector"
import formatAuthCode from "formatAuthCode"

class KitchenStationForm extends React.Component {
  state = {}

  componentDidMount() {
    const id = this.props.computedMatch.params.stationId
    if (id) {
      this.props.fetchData(`venue_admin/kitchen_stations/${id}/edit`, this.props.seedFormData)
    } else {
      this.props.fetchData("venue_admin/kitchen_stations/new", this.props.seedFormData)
    }
  }

  toggleCategory = (categoryId) => {
    const current = this.props.formData.categoryIds || []
    const next = current.includes(categoryId)
      ? current.filter((id) => id !== categoryId)
      : [...current, categoryId]
    this.props.updateField("categoryIds", next)
  }

  prepMinutes = () => Math.round((this.props.formData.prepTimeSeconds || 600) / 60)

  setPrepMinutes = (raw) => {
    const minutes = Math.max(1, parseInt(raw, 10) || 0)
    this.props.updateField("prepTimeSeconds", minutes * 60)
  }

  handleSubmit = (e) => {
    e.preventDefault()
    const id = this.props.formData.id
    const method = id ? "patch" : "post"
    const url = id ? `kitchen_stations/${id}` : "kitchen_stations"

    const station = {
      name: this.props.formData.name,
      color: this.props.formData.color,
      prep_time_seconds: this.props.formData.prepTimeSeconds,
      position: this.props.formData.position || 0,
      is_default: this.props.formData.isDefault || false,
      printer_id: this.props.formData.printerId || null,
    }
    const category_ids = this.props.formData.categoryIds || []
    const terminal_ids = this.props.formData.terminalIds || []
    const store_ids = this.props.formData.storeIds || []

    request[method](`venue_admin/${url}`, { station, category_ids, terminal_ids, store_ids }).then(
      ({ data }) => {
        if (data.success) this.setState({ saved: true })
      }
    )
  }

  render() {
    if (!this.props.dataFetched) return false
    if (this.state.saved) return <Redirect to="/kitchen-stations" />

    const { formData, updateField } = this.props
    const selectedIds = new Set(formData.categoryIds || [])
    const stores = this.props.stores || []
    const printers = this.props.printers || []

    return (
      <div className="KitchenStationForm">
        <Header text="Kitchen Stations" backLink="/kitchen-stations" />
        <Form
          title={formData.id ? `Edit ${formData.name}` : "New Kitchen Station"}
          onSubmit={this.handleSubmit}
        >
          <Input
            label="Station Name"
            value={formData.name || ""}
            onChange={updateField.bind(null, "name")}
          />

          {formData.authCode && (
            <div className="block">
              <label className="label">Station login code</label>
              <p className="has-text-grey">
                Enter this code on the Kitchen Display device (kds subdomain) to
                log it in as this station.
              </p>
              <code className="station-auth-code">{formatAuthCode(formData.authCode)}</code>
            </div>
          )}

          <div className="block">
            <label className="label">Display color</label>
            <div className="color-row">
              <input
                type="color"
                value={formData.color || "#6b7280"}
                onChange={(e) => updateField("color", e.target.value)}
              />
              <span className="has-text-grey">{formData.color || "#6b7280"}</span>
            </div>
          </div>

          <Input
            label="Prep target (minutes)"
            type="number"
            min={1}
            value={this.prepMinutes()}
            onChange={this.setPrepMinutes}
          />

          <div className="routing-note">
            <strong>How routing works.</strong> An order is sent to this station only
            when it matches <em>every</em> filter you set below. If you pick both
            categories and terminals, an order must match one of the categories{" "}
            <strong>and</strong> come from one of the selected terminals or stores.
            Leave a section empty to put no restriction on it — e.g. no terminals
            selected means any terminal qualifies.
          </div>

          <div className="block">
            <label className="label">Categories routed to this station</label>
            {(this.props.categories || []).length === 0 ? (
              <p className="has-text-grey is-italic">
                No product categories defined yet. Create categories first to route orders to this station.
              </p>
            ) : (
              <div className="category-list">
                {this.props.categories.map((cat) => {
                  const checked = selectedIds.has(cat.id)
                  return (
                    <label key={cat.id} className={`category-chip ${checked ? "checked" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => this.toggleCategory(cat.id)}
                      />
                      {cat.name}
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          <div className="block">
            <label className="label">Terminals routed to this station</label>
            <StoreTerminalSelector
              stores={stores}
              storeIds={formData.storeIds || []}
              terminalIds={formData.terminalIds || []}
              onStoreIdsChange={(ids) => updateField("storeIds", ids)}
              onTerminalIdsChange={(ids) => updateField("terminalIds", ids)}
            />
          </div>

          <div className="block">
            <label className="label">Printer</label>
            <p className="printer-note">
              Optionally attach a printer. When set, each order routed to this
              station is automatically printed on it the moment it arrives — handy
              for stations without a screen. Leave as <em>No printer</em> to only
              show orders on the display.
            </p>
            <div className="select is-fullwidth">
              <select
                value={formData.printerId || ""}
                onChange={(e) => updateField("printerId", e.target.value || null)}
              >
                <option value="">No printer</option>
                {printers.map((printer) => (
                  <option key={printer.id} value={printer.id}>
                    {printer.name}
                  </option>
                ))}
              </select>
            </div>
            {printers.length === 0 && (
              <p className="has-text-grey is-italic">
                No printers set up for this venue yet.
              </p>
            )}
          </div>

          <div className="block field is-grouped">
            <p className="control">
              <button onClick={this.handleSubmit} className="button is-primary">
                {formData.id ? "Update" : "Create"} Station
              </button>
            </p>
            <p className="control">
              <Link to="/kitchen-stations" className="button">Cancel</Link>
            </p>
          </div>
        </Form>
      </div>
    )
  }
}

export default usesFeatures(["form", "data"], KitchenStationForm)
